import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg, fmtDate, dueClass } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Search, Sparkles } from "lucide-react";
import { FineAppealDialog } from "@/components/FineAppealDialog";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { vehicle_id: "", reference: "", date: "", due_date: "", amount: "", location: "", reason: "", driver_name: "", issuer: "" };
const STATUS_STYLE = {
  unpaid: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10",
  paid: "text-emerald-600 dark:text-[#32D74B] border-emerald-600/40 dark:border-[#32D74B]/40 bg-emerald-600/10 dark:bg-[#32D74B]/10",
  appealed: "text-amber-600 dark:text-[#FFD60A] border-amber-600/40 dark:border-[#FFD60A]/40 bg-amber-600/10 dark:bg-[#FFD60A]/10",
};

export default function FinesPage() {
  const { canEdit } = useAuth();
  const editable = canEdit("fines");
  const [fines, setFines] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("");
  const [appealFine, setAppealFine] = useState(null);

  const load = () => {
    api.get("/fines").then((r) => setFines(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    if (!form.vehicle_id || !form.reference || !form.date || !form.amount) return toast.error("Vehicle, reference, date and amount are required");
    try {
      const payload = { ...form, amount: Number(form.amount) };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await api.post("/fines", payload);
      toast.success("PCN fine recorded");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const setStatus = async (f, status) => {
    await api.put(`/fines/${f.id}`, { status });
    toast.success(`Fine ${f.reference} marked ${status}`);
    load();
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete fine ${f.reference}?`)) return;
    await api.delete(`/fines/${f.id}`);
    load();
  };

  const unpaidTotal = fines.filter((f) => f.status === "unpaid").reduce((a, f) => a + f.amount, 0);
  const shown = fines.filter((f) => [f.reference, f.vehicle_reg, f.driver_name, f.issuer, f.location, f.reason, f.status].join(" ").toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="p-8" data-testid="fines-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">PCN FINES</h1>
          <p className="text-muted-foreground text-sm mt-1">{fines.length} fines · <span className="text-[#FF3B30] font-bold">£{unpaidTotal.toFixed(2)} unpaid</span></p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="fine-search-input" placeholder="Search ref, vehicle, driver…" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-none bg-card border-border pl-9 w-64 h-10" />
          </div>
          {editable && (
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-fine-button" className="rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-10">
              <Plus size={15} className="mr-1" /> Record fine
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border rounded-none text-foreground">
            <DialogHeader><DialogTitle className="font-heading font-black tracking-tighter text-2xl">RECORD PCN FINE</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Vehicle *</Label>
                <Select value={form.vehicle_id} onValueChange={(val) => setForm((f) => ({ ...f, vehicle_id: val, driver_name: f.driver_name || vehicles.find((x) => x.id === val)?.driver_name || "" }))}>
                  <SelectTrigger data-testid="fine-vehicle-select" className="mt-1.5 rounded-none bg-background border-border h-9"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                  <SelectContent className="bg-card border-border text-foreground rounded-none">
                    {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration} — {v.make || ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["PCN reference *", "reference", "text", "fine-reference-input"],
                  ["Date *", "date", "date", "fine-date-input"],
                  ["Amount (£) *", "amount", "number", "fine-amount-input"],
                  ["Pay by (before increase)", "due_date", "date", "fine-duedate-input"],
                  ["Location", "location", "text", "fine-location-input"],
                  ["Driver", "driver_name", "text", "fine-driver-input"],
                  ["Issuing company", "issuer", "text", "fine-issuer-input"],
                ].map(([label, key, type, tid]) => (
                  <div key={key}>
                    <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{label}</Label>
                    <Input data-testid={tid} type={type} value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} className="mt-1.5 rounded-none bg-background border-border h-9" />
                  </div>
                ))}
              </div>
              <div>
                <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Reason</Label>
                <Input data-testid="fine-reason-input" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Parking in restricted zone" className="mt-1.5 rounded-none bg-background border-border h-9" />
              </div>
              <Button data-testid="save-fine-button" onClick={save} className="w-full rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-11">Save fine</Button>
            </div>
          </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <div className="border border-border bg-card overflow-x-auto overflow-y-auto max-h-[calc(100vh-230px)]">
        <table className="w-full text-sm" data-testid="fines-table">
          <thead className="sticky top-0 z-20 bg-card shadow-[0_1px_0_0_hsl(var(--border))]">
            <tr className="text-left">
              {["Reference", "Vehicle", "Driver", "Company", "Date", "Pay by", "Amount", "Reason", "Paid on", "Appealed on", "Status", ""].map((h, i) => (
                <th key={i} className="px-4 py-3 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((f) => (
              <tr key={f.id} data-testid={`fine-row-${f.reference}`} className="border-b border-border/60 hover:bg-muted/60">
                <td className="px-4 py-3 font-mono font-bold whitespace-nowrap">{f.reference}</td>
                <td className="px-4 py-3 font-mono text-foreground/80 whitespace-nowrap">{f.vehicle_reg}</td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{f.driver_name || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{f.issuer || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(f.date)}</td>
                <td className={`px-4 py-3 whitespace-nowrap ${f.status === "unpaid" ? dueClass(f.due_date) : "text-muted-foreground/60"}`}>{fmtDate(f.due_date)}</td>
                <td className="px-4 py-3 font-bold whitespace-nowrap">£{f.amount.toFixed(2)}</td>
                <td className="px-4 py-3 text-muted-foreground">{f.reason || "—"}</td>
                <td className="px-4 py-3 text-emerald-600 dark:text-[#32D74B] whitespace-nowrap">{f.paid_date ? fmtDate(f.paid_date) : "—"}</td>
                <td className="px-4 py-3 text-amber-600 dark:text-[#FFD60A] whitespace-nowrap">{f.appealed_date ? fmtDate(f.appealed_date) : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`border px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${STATUS_STYLE[f.status]}`}>{f.status}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <button data-testid={`appeal-fine-${f.reference}`} onClick={() => setAppealFine(f)} className="text-[10px] uppercase tracking-widest font-bold text-foreground inline-flex items-center gap-1 border border-border px-2 py-1 hover:bg-muted transition-colors duration-150">
                      <Sparkles size={11} /> Appeal{f.appeal_text ? " ✓" : ""}
                    </button>
                    {editable && (
                    <>
                    {f.status !== "paid" && (
                      <button data-testid={`mark-paid-${f.reference}`} onClick={() => setStatus(f, "paid")} className="text-[10px] uppercase tracking-widest font-bold text-emerald-600 dark:text-[#32D74B] hover:underline">Paid</button>
                    )}
                    {f.status === "unpaid" && (
                      <button data-testid={`mark-appealed-${f.reference}`} onClick={() => setStatus(f, "appealed")} className="text-[10px] uppercase tracking-widest font-bold text-amber-600 dark:text-[#FFD60A] hover:underline">Mark appealed</button>
                    )}
                    <button data-testid={`delete-fine-${f.reference}`} onClick={() => remove(f)} className="text-muted-foreground/70 hover:text-[#FF3B30] transition-colors duration-150"><Trash2 size={14} /></button>
                    </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-12 text-center text-muted-foreground/70">{fines.length === 0 ? "No PCN fines recorded. Long may it continue." : "No fines match your search."}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <FineAppealDialog fine={appealFine} editable={editable} onClose={() => setAppealFine(null)} onSaved={load} />
    </div>
  );
}
